/**
 * Hook réutilisable — détecte si la saisie des notes est verrouillée
 * pour le semestre courant (fix 2026-08-28).
 *
 * Utilisé dans SaisieMatricielle (devoirs), SaisieMatricielleExamens,
 * SaisieIndividuelleExamens, SaisieLigneExamens — pour afficher un bandeau
 * rouge visible et désactiver toute saisie quand un semestre est clôturé.
 *
 * Vérifie 5 paramètres (le premier qui bloque emporte) :
 *   - saisie_notes_verrouillee (global)
 *   - saisie_notes_verrouillee_s1 / _s2 (par parité)
 *   - saisie_notes_verrouillee_s1_ord / _ratt / _s2_ord / _ratt (session × parité)
 */
import { useMemo } from 'react';
import { useGetParametreByKeyQuery } from '../services/coreApi';

const isVerrouActif = (p: any): boolean => {
  if (!p) return false;
  const v = (p.valeur_convertie ?? p.valeur ?? '').toString().toLowerCase().trim();
  return v === 'true' || v === '1' || v === 'oui' || v === 'on';
};

export interface SemestreClotureEtat {
  motif: string;
  label: string;
}

export function useSemestreCloture(
  semestre: 'S1' | 'S2',
  estRattrapage: boolean = false,
  // Fix 2026-10-15 : les verrous `saisie_notes_verrouillee_*` vivent dans le
  // schéma `config` (partagé) → ils sont GLOBAUX et ne concernent QUE l'année
  // ACTIVE. Quand on saisit une année ANTÉRIEURE, ces verrous ne s'appliquent
  // pas (chaque année a ses propres verrous/clôture). Passer `estAnneeActive`
  // = false désactive donc la détection de clôture ici.
  estAnneeActive: boolean = true,
): SemestreClotureEtat | null {
  const { data: pGlobal } = useGetParametreByKeyQuery('saisie_notes_verrouillee');
  const { data: pS1 } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s1');
  const { data: pS2 } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s2');
  const { data: pS1Ord } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s1_ord');
  const { data: pS1Ratt } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s1_ratt');
  const { data: pS2Ord } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s2_ord');
  const { data: pS2Ratt } = useGetParametreByKeyQuery('saisie_notes_verrouillee_s2_ratt');

  return useMemo(() => {
    // Année antérieure → aucun verrou opérationnel de l'année active.
    if (!estAnneeActive) return null;
    if (isVerrouActif(pGlobal)) return { motif: 'global', label: 'Saisie fermée globalement' };
    if (semestre === 'S1') {
      if (isVerrouActif(pS1)) return { motif: 's1', label: 'Semestre impair (S1) clôturé' };
      if (estRattrapage && isVerrouActif(pS1Ratt))
        return { motif: 's1_ratt', label: 'Session rattrapage S1 clôturée' };
      if (!estRattrapage && isVerrouActif(pS1Ord))
        return { motif: 's1_ord', label: 'Session ordinaire S1 clôturée' };
    }
    if (semestre === 'S2') {
      if (isVerrouActif(pS2)) return { motif: 's2', label: 'Semestre pair (S2) clôturé' };
      if (estRattrapage && isVerrouActif(pS2Ratt))
        return { motif: 's2_ratt', label: 'Session rattrapage S2 clôturée' };
      if (!estRattrapage && isVerrouActif(pS2Ord))
        return { motif: 's2_ord', label: 'Session ordinaire S2 clôturée' };
    }
    return null;
  }, [pGlobal, pS1, pS2, pS1Ord, pS1Ratt, pS2Ord, pS2Ratt, semestre, estRattrapage, estAnneeActive]);
}
